import { useState } from 'react';

/**
 * Custom hook for managing alert notifications
 * Provides a simple API for showing alerts with automatic dismissal
 *
 * @returns {Object} Alert management functions and state
 */

export const useAlert = () => {
  /**
   * Hide the current alert
   */
  const hideAlert = () => setAlert(prev => ({ ...prev, isOpen: false }));

  const [alert, setAlert] = useState({
    isOpen: false,
    message: '',
    variant: 'success',
    duration: 2000,
    onClose: hideAlert,
  });

  /**
   * Show an alert with the specified message and options
   * @param {string} message - The alert message to display
   * @param {Object} options - Alert options
   * @param {string} options.variant - Alert variant: 'success', 'error', 'warning' (default: 'success')
   * @param {number} options.duration - Auto-dismiss duration in ms (default: 2000)
   * @param {boolean} options.persistent - If true, alert won't auto-dismiss (default: false)
   */
  const showAlert = (message, options = {}) => {
    const { variant = 'success', duration = 2000, persistent = false } = options;

    setAlert({
      isOpen: true,
      message,
      variant,
      duration: persistent ? 0 : duration,
      onClose: hideAlert,
    });
  };

  /**
   * Convenience methods for common alert types
   */
  const alertHelpers = {
    success: (message, duration) => showAlert(message, { variant: 'success', duration }),
    error: (message, duration) => showAlert(message, { variant: 'error', duration }),
    warning: (message, duration) => showAlert(message, { variant: 'warning', duration }),
    persistent: (message, variant = 'success') => showAlert(message, { variant, persistent: true }),
  };

  return {
    alert,
    showAlert,
    hideAlert,
    ...alertHelpers,
  };
};
